import { ArrowLeft, ArrowRight, ArrowUpRight, Shield, Zap, Lock, Headphones } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import PriceTicker from "./PriceTicker";
import PopularBadge from "@/components/common/PopularBadge";
import { useLiveMarket } from "@/lib/useLiveMarket";

const HERO_TRUST = [
  { icon: Shield, label: "FIU-IND aligned" },
  { icon: Lock, label: "KYC & AML" },
  { icon: Zap, label: "Fast execution" },
  { icon: Headphones, label: "24/7 support" },
];

const heroSlides = [
  { src: "/assets/safari-hero-1.png", alt: "CoinMBX dashboard preview 1" },
  { src: "/assets/safari-hero-2.png", alt: "CoinMBX dashboard preview 2" },
];

const productPills = [
  { label: "Spot", href: "/trade/spot/BTC-USDT" },
  { label: "OTC", href: "/otc", badge: "Popular" },
];

function fmtBtc(price) {
  if (!Number.isFinite(price)) return null;
  return price.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export default function Hero() {
  const [slide, setSlide] = useState(0);
  const [hovered, setHovered] = useState(false);
  const total = heroSlides.length;
  const timerRef = useRef(null);
  const live = useLiveMarket(["bitcoin"]);
  const btc = live.bitcoin;

  useEffect(() => {
    if (hovered) return;
    timerRef.current = setInterval(() => {
      setSlide((s) => (s + 1) % total);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [hovered, total]);

  const prev = () => setSlide((s) => (s - 1 + total) % total);
  const next = () => setSlide((s) => (s + 1) % total);

  return (
    <section className="relative overflow-hidden cm-landing-hero-bg cm-landing-mesh" data-testid="hero-section">
      <div className="absolute inset-0 cm-landing-grid-bg opacity-50 pointer-events-none" />

      <div className="relative border-b border-[var(--cm-border)]/80 bg-white/80 backdrop-blur-sm">
        <Header />
      </div>
      <PriceTicker />

      <div className="relative cm-container">
        <div className="grid lg:grid-cols-[1fr_1.05fr] gap-12 lg:gap-16 items-center pt-10 pb-16 lg:pt-14 lg:pb-24">
          <div className="cm-fade-up max-w-[620px]" data-testid="hero-copy">
            <span className="cm-landing-eyebrow">
              <Shield size={14} strokeWidth={2.2} aria-hidden />
              Regulated crypto exchange
            </span>

            <h1 className="cm-landing-hero-title mt-6">
              Trade spot
              <br />
              <span className="cm-landing-hero-accent">&amp; OTC on CoinMBX</span>
            </h1>

            <p className="mt-6 text-[16px] lg:text-[17px] leading-[1.75] text-cm-grey max-w-[520px]">
              Elevate your crypto trading with deep liquidity, efficient margining, INR-friendly
              flows, and professional tools — built for India and beyond.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              {productPills.map((p) => (
                <Link
                  key={p.label}
                  to={p.href}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-cm-navy bg-white border border-[var(--cm-border)] hover:border-cm-blue/40 hover:text-cm-blue transition-colors"
                  data-testid={`hero-pill-${p.label.toLowerCase()}`}
                >
                  {p.label}
                  {p.badge && <PopularBadge testId="hero-otc-popular-badge" />}
                  <ArrowUpRight size={14} className="opacity-60" />
                </Link>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/register"
                data-testid="hero-start-trading"
                className="inline-flex items-center justify-center h-[52px] px-10 rounded-full bg-cm-blue text-white font-bold text-[15px] shadow-[0_16px_40px_-14px_rgba(2,44,237,0.55)] hover:bg-cm-blue-deep hover:-translate-y-0.5 transition-all duration-200"
              >
                Sign up
              </Link>
              <Link
                to="/trade/spot/BTC-USDT"
                data-testid="hero-view-exchange"
                className="inline-flex items-center justify-center h-[52px] px-10 rounded-full bg-white text-cm-blue font-bold text-[15px] border-2 border-cm-blue/20 hover:border-cm-blue hover:bg-cm-lavender/50 transition-all duration-200"
              >
                Start trading
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap gap-2.5">
              {HERO_TRUST.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 border border-[var(--cm-border)] text-[11px] font-semibold text-cm-navy shadow-sm"
                >
                  <Icon size={13} className="text-cm-blue shrink-0" strokeWidth={2} />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div
            className="relative cm-fade-up hidden lg:block"
            style={{ animationDelay: "120ms" }}
            data-testid="hero-visual"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <div
              className="absolute -inset-6 rounded-[32px] opacity-40 blur-3xl pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 50% 40%, rgba(2,44,237,0.35) 0%, transparent 70%)",
              }}
            />
            <div className="relative rounded-[20px] border border-[var(--cm-border)] bg-white p-3 shadow-[0_40px_80px_-32px_rgba(29,45,63,0.28)]">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--cm-border)] mb-2">
                <span className="text-[11px] font-bold tracking-wider text-cm-grey uppercase">
                  Live platform
                </span>
                <span className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Markets open
                </span>
              </div>
              {btc && (
                <div
                  className="absolute top-14 right-5 z-10 rounded-[12px] border border-[var(--cm-border)] bg-white/95 backdrop-blur px-3 py-2 shadow-lg"
                  data-testid="hero-btc-chip"
                >
                  <div className="text-[10px] font-bold text-cm-grey uppercase tracking-wider">BTC</div>
                  <div className="text-[15px] font-extrabold text-cm-navy tabular-nums">
                    {fmtBtc(btc.price)}
                  </div>
                  <div
                    className={`text-[11px] font-bold tabular-nums ${btc.up ? "text-emerald-600" : "text-red-500"}`}
                  >
                    {btc.up ? "+" : ""}
                    {btc.changePct?.toFixed(2)}%
                  </div>
                </div>
              )}
              <HeroSlider slide={slide} />
              <div className="flex items-center justify-between mt-4 px-2">
                <div className="flex items-center gap-2">
                  {heroSlides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setSlide(i)}
                      data-testid={`hero-dot-${i}`}
                      className="rounded-full transition-all duration-300"
                      style={{
                        width: slide === i ? 24 : 8,
                        height: 8,
                        background: slide === i ? "#022dee" : "#E7E9F2",
                      }}
                      aria-label={`Go to slide ${i + 1}`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={prev}
                    data-testid="hero-prev"
                    className="w-10 h-10 rounded-full border border-[var(--cm-border)] grid place-items-center text-cm-navy hover:bg-cm-surface transition-all"
                    aria-label="Previous"
                  >
                    <ArrowLeft size={18} strokeWidth={1.6} />
                  </button>
                  <button
                    onClick={next}
                    data-testid="hero-next"
                    className="w-10 h-10 rounded-full border border-[var(--cm-border)] grid place-items-center text-cm-navy hover:bg-cm-blue hover:text-white hover:border-cm-blue transition-all"
                    aria-label="Next"
                  >
                    <ArrowRight size={18} strokeWidth={1.6} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile hero visual */}
        <div
          className="lg:hidden pb-12 cm-fade-up"
          data-testid="hero-visual-mobile"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div className="rounded-[16px] border border-[var(--cm-border)] bg-white p-2 shadow-lg">
            <HeroSlider slide={slide} />
          </div>
          <div className="flex items-center justify-center gap-2 mt-4">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSlide(i)}
                className="rounded-full transition-all"
                style={{
                  width: slide === i ? 20 : 8,
                  height: 8,
                  background: slide === i ? "#022dee" : "#E7E9F2",
                }}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroSlider({ slide }) {
  return (
    <div
      className="relative rounded-[14px] overflow-hidden bg-cm-surface"
      style={{ aspectRatio: "703 / 800" }}
      data-testid="hero-slider"
    >
      {heroSlides.map((s, i) => {
        const isActive = i === slide;
        const offset = (i - slide) * 40;
        return (
          <img
            key={s.src}
            src={s.src}
            alt={s.alt}
            draggable={false}
            data-testid={`hero-slide-${i}`}
            className="absolute inset-0 w-full h-full object-cover select-none"
            style={{
              opacity: isActive ? 1 : 0,
              transform: `translateX(${isActive ? 0 : offset}px) scale(${isActive ? 1 : 0.98})`,
              transition:
                "opacity 700ms cubic-bezier(0.4, 0, 0.2, 1), transform 700ms cubic-bezier(0.4, 0, 0.2, 1)",
              pointerEvents: isActive ? "auto" : "none",
            }}
          />
        );
      })}
    </div>
  );
}
